import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [running, setRunning] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ slug: "", title: "", nav_label: "", in_nav: true, order: 100 });

  const load = () => api.get("/pages").then(r => setPages(r.data || []));
  useEffect(() => {
    load();
    api.get("/admin/migrations").then(r => setMigrations(r.data || [])).catch(() => {});
  }, []);

  const runMigration = async (m) => {
    if (!window.confirm(`This will REPLACE the current content of "/${m.target_slug}" with the canonical version. Continue?`)) return;
    setRunning(m.key);
    try {
      const r = await api.post(`/admin/migrations/${m.key}/run`);
      toast.success(`Page ${r.data.action}: /${r.data.slug} (${r.data.blocks} blocks)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration failed");
    } finally {
      setRunning(null);
    }
  };

  const create = async () => {
    if (!form.slug || !form.title) { toast.error("Slug and title are required"); return; }
    try {
      await api.post("/pages", { ...form, blocks: [] });
      toast.success("Page created");
      setCreateOpen(false);
      setForm({ slug: "", title: "", nav_label: "", in_nav: true, order: 100 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create page");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete page "${p.title}"?`)) return;
    try {
      await api.delete(`/pages/${p.id}`);
      toast.success("Page deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleNav = async (p) => {
    await api.patch(`/pages/${p.id}`, { in_nav: !p.in_nav });
    load();
  };

  return (
    <div className="p-5 sm:p-8 lg:p-10" data-testid="admin-pages">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="label-eyebrow mb-3">Content</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1a1a1a]">Pages</h1>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary-burgundy" data-testid="new-page-btn"><Plus size={14} /> New page</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif-display text-2xl">Create page</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="About us" data-testid="page-title-input" />
              </div>
              <div>
                <Label>URL slug</Label>
                <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="about-us" data-testid="page-slug-input" />
              </div>
              <div>
                <Label>Nav label (optional)</Label>
                <Input value={form.nav_label} onChange={e => setForm({ ...form, nav_label: e.target.value })} placeholder="About" />
              </div>
              <div className="flex items-center justify-between">
                <Label>Show in navigation</Label>
                <Switch checked={form.in_nav} onCheckedChange={(v) => setForm({ ...form, in_nav: v })} />
              </div>
              <div>
                <Label>Order</Label>
                <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })} />
              </div>
            </div>
            <DialogFooter>
              <button className="btn-outline-burgundy" onClick={() => setCreateOpen(false)}>Cancel</button>
              <button className="btn-primary-burgundy" onClick={create} data-testid="page-create-submit">Create</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {migrations.length > 0 && (
        <div className="mb-6 border border-amber-200 bg-amber-50 p-4" data-testid="migrations-panel">
          <p className="label-eyebrow text-amber-900 mb-2">Canonical content sync</p>
          <p className="text-sm text-amber-900/80 mb-3">
            Rebuild a page to match the printed/handout version. <strong>This overwrites the current blocks.</strong>
          </p>
          <div className="space-y-2">
            {migrations.map((m) => (
              <div key={m.key} className="flex items-center justify-between gap-3 bg-white border border-amber-200 px-4 py-3 rounded">
                <div className="text-sm">
                  <p className="font-medium text-neutral-900">{m.description}</p>
                  <p className="text-xs text-neutral-500">Target: /{m.target_slug}</p>
                </div>
                <button
                  className="btn-outline-burgundy whitespace-nowrap text-sm inline-flex items-center gap-2"
                  onClick={() => runMigration(m)}
                  disabled={running === m.key}
                  data-testid={`run-migration-${m.key}`}
                >
                  <RefreshCw size={14} className={running === m.key ? "animate-spin" : ""} />
                  {running === m.key ? "Running…" : "Run sync"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-6 py-4">Title</th>
              <th className="px-6 py-4">Slug</th>
              <th className="px-6 py-4">In nav</th>
              <th className="px-6 py-4">Order</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => (
              <tr key={p.id} className="border-b border-neutral-100 last:border-0" data-testid={`page-row-${p.slug}`}>
                <td className="px-6 py-4">
                  <p className="font-medium text-[#1a1a1a]">{p.title}</p>
                  {p.is_system && <span className="text-xs text-[#6B1F2A] tracking-wider uppercase">System {p.system_kind ? `· ${p.system_kind}` : ""}</span>}
                </td>
                <td className="px-6 py-4 text-sm text-neutral-600">/{p.slug}</td>
                <td className="px-6 py-4"><Switch checked={p.in_nav} onCheckedChange={() => toggleNav(p)} /></td>
                <td className="px-6 py-4 text-sm">{p.order}</td>
                <td className="px-6 py-4 text-right">
                  <div className="inline-flex gap-2">
                    <Link to={`/admin/pages/${p.id}`} className="p-2 hover:bg-neutral-100 rounded" title="Edit" data-testid={`edit-page-${p.slug}`}>
                      <Pencil size={14} />
                    </Link>
                    {!p.is_system && (
                      <button onClick={() => remove(p)} className="p-2 hover:bg-red-50 hover:text-red-600 rounded" title="Delete" data-testid={`delete-page-${p.slug}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
